<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import {
  Card,
  CardHeader,
  CardContent,
} from 'dashboard/components-next/ui-shadcn/card';

const props = defineProps({
  icon: { type: String, required: true },
  title: { type: String, required: true },
  value: { type: Number, default: 0 },
  previous: { type: Number, default: 0 },
});

const { t } = useI18n();

const display = computed(() => (props.value ? formatTime(props.value) : '--'));

// Percentage change vs the previous period. For time metrics, a decrease is an
// improvement, so a negative delta is rendered as positive (green/down arrow).
const trend = computed(() => {
  if (!props.previous || !props.value) return null;
  const delta = ((props.value - props.previous) / props.previous) * 100;
  return {
    percent: Math.abs(Math.round(delta)),
    improved: delta <= 0,
  };
});
</script>

<template>
  <Card>
    <CardHeader>
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="size-10 rounded-xl bg-n-alpha-2 dark:bg-n-alpha-1 flex items-center justify-center text-n-slate-12 shrink-0"
        >
          <span class="size-5" :class="[icon]" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 truncate">
          {{ title }}
        </h3>
      </div>
    </CardHeader>
    <CardContent class="pt-0">
      <p class="text-4xl font-bold text-n-slate-12">{{ display }}</p>
      <p
        v-if="trend"
        class="text-xs mt-2 flex items-center gap-1"
        :class="trend.improved ? 'text-n-teal-10' : 'text-n-ruby-10'"
      >
        <span
          class="size-3.5"
          :class="[
            trend.improved
              ? 'i-lucide-arrow-down-right'
              : 'i-lucide-arrow-up-right',
          ]"
        />
        {{ t('PAINEL_PRINCIPAL.TIME.VS_PREVIOUS', { percent: trend.percent }) }}
      </p>
    </CardContent>
  </Card>
</template>
